/**
 * PackagesPage — CRUD cho bảng packages (Sprint 5).
 * Type `Package` fields: package (PK), package_eng, premium, is_status.
 */
import { useEffect, useMemo, useState } from 'react'
import {
  Box,
  Button,
  IconButton,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import AddIcon from '@mui/icons-material/Add'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import type { GridColDef, GridPaginationModel } from '@mui/x-data-grid'
import { useForm } from 'react-hook-form'

import ConfirmDialog from '@/components/ConfirmDialog'
import DataTableMUI from '@/components/DataTableMUI'
import FormDialog from '@/components/FormDialog'
import PageDataState from '@/components/PageDataState'
import RoleGuard from '@/components/RoleGuard'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useListPackagesCrudQuery,
  useCreatePackageMutation,
  useUpdatePackageMutation,
  useDeletePackageMutation,
} from '@/store/api/masterDataCrudApi'
import { useDebounce } from '@/hooks/useDebounce'
import { useSnackbar } from '@/hooks/useSnackbar'
import { getErrorMessage } from '@/utils/error'
import { formatNumber } from '@/utils/money'
import type { Package } from '@/types/api'

interface PkgFormValues {
  package: string
  package_eng: string
  premium: string
}

const EMPTY: PkgFormValues = { package: '', package_eng: '', premium: '' }

export function PackagesPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()

  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search, 350)
  const [pagination, setPagination] = useState<GridPaginationModel>({ page: 0, pageSize: 25 })
  const [editing, setEditing] = useState<Package | null>(null)
  const [createOpen, setCreateOpen] = useState(false)
  const [confirmDelete, setConfirmDelete] = useState<Package | null>(null)

  const { data, isLoading, error, isFetching } = useListPackagesCrudQuery({
    q: debouncedSearch || undefined,
    page: pagination.page + 1,
    page_size: pagination.pageSize,
  })

  const [createPackage, { isLoading: creating }] = useCreatePackageMutation()
  const [updatePackage, { isLoading: updating }] = useUpdatePackageMutation()
  const [deletePackage, { isLoading: deleting }] = useDeletePackageMutation()

  const dialogOpen = createOpen || !!editing
  const isEdit = !!editing
  const dialogLoading = creating || updating

  const { register, handleSubmit, reset } = useForm<PkgFormValues>({ defaultValues: EMPTY })

  useEffect(() => {
    if (!dialogOpen) return
    if (isEdit && editing) {
      reset({
        package: editing.package,
        package_eng: editing.package_eng ?? '',
        premium: editing.premium != null ? String(editing.premium) : '',
      })
    } else {
      reset(EMPTY)
    }
  }, [dialogOpen, isEdit, editing, reset])

  const rows = useMemo(
    () => (data?.items ?? []).map((r) => ({ id: r.package, ...r })),
    [data],
  )

  const columns = useMemo<GridColDef[]>(
    () => [
      { field: 'package', headerName: 'Package Code', width: 160 },
      { field: 'package_eng', headerName: 'Package (Eng)', flex: 1, minWidth: 200 },
      {
        field: 'premium',
        headerName: 'Premium',
        width: 130,
        valueFormatter: (value) => formatNumber(value as number | null),
      },
      {
        field: '_actions',
        headerName: 'Actions',
        width: 120,
        sortable: false,
        filterable: false,
        renderCell: (params) => (
          <Stack direction="row" spacing={0.5}>
            <Tooltip title="Edit">
              <IconButton size="small" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setEditing(params.row as Package) }}>
                <EditIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <Tooltip title="Delete">
              <IconButton size="small" color="error" disabled={!canEdit} onClick={(e) => { e.stopPropagation(); setConfirmDelete(params.row as Package) }}>
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        ),
      },
    ],
    [],
  )

  const handleCloseDialog = () => { setCreateOpen(false); setEditing(null) }

  const onSubmit = handleSubmit(async (values) => {
    const payload: Package = {
      package: values.package.trim(),
      package_eng: values.package_eng || null,
      premium: values.premium !== '' ? Number(values.premium) : null,
    }
    try {
      if (isEdit && editing) {
        await updatePackage({ id: editing.package, body: payload }).unwrap()
        snack.success(`Updated "${editing.package}"`)
      } else {
        await createPackage(payload).unwrap()
        snack.success(`Created "${payload.package}"`)
      }
      handleCloseDialog()
    } catch (err) {
      snack.error(getErrorMessage(err, isEdit ? 'Update failed' : 'Create failed'))
    }
  })

  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try {
      await deletePackage(confirmDelete.package).unwrap()
      snack.success(`Deleted "${confirmDelete.package}"`)
      setConfirmDelete(null)
    } catch (err) {
      snack.error(getErrorMessage(err, 'Delete failed'))
    }
  }

  const isEmpty = !isLoading && !error && (data?.items?.length ?? 0) === 0

  return (
    <Box display="flex" flexDirection="column" gap={2}>
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="h5" fontWeight={700}>Packages</Typography>
        <Button variant="contained" startIcon={<AddIcon />} disabled={!canEdit} onClick={() => setCreateOpen(true)}>
          New Package
        </Button>
      </Box>

      <Paper sx={{ p: 2 }}>
        <TextField
          label="Search"
          placeholder="Package code..."
          size="small"
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPagination((p) => ({ ...p, page: 0 })) }}
          sx={{ minWidth: 260 }}
        />
      </Paper>

      <PageDataState loading={isLoading} error={error} empty={isEmpty}>
        <Paper>
          <DataTableMUI
            rows={rows}
            columns={columns}
            loading={isFetching}
            paginationMode="server"
            paginationModel={pagination}
            onPaginationModelChange={setPagination}
            rowCount={data?.total ?? 0}
            onRowClick={(p) => setEditing(p.row as Package)}
          />
        </Paper>
      </PageDataState>

      <FormDialog
        open={dialogOpen}
        title={isEdit ? 'Edit Package' : 'New Package'}
        loading={dialogLoading}
        onClose={handleCloseDialog}
        onSubmit={() => void onSubmit()}
      >
        <Box display="flex" flexDirection="column" gap={2} pt={1}>
          <TextField label="Package Code *" size="small" fullWidth {...register('package')} disabled={isEdit} />
          <TextField label="Package (Eng)" size="small" fullWidth {...register('package_eng')} />
          <TextField label="Premium" size="small" fullWidth type="number" {...register('premium')} />
        </Box>
      </FormDialog>

      <ConfirmDialog
        open={!!confirmDelete}
        title="Delete Package"
        message={confirmDelete ? `Are you sure you want to delete "${confirmDelete.package}"?` : ''}
        destructive
        loading={deleting}
        onConfirm={() => void handleConfirmDelete()}
        onCancel={() => setConfirmDelete(null)}
      />
    </Box>
  )
}

export default PackagesPage
